/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { ShowMoreGridSection } from '@coursera-web/cds-labs';
import type { ExpandButtonProps } from '@coursera-web/cds-labs';
import { Specialization_ProductVariant } from '__generated__/graphql-types';

import { Button, CardStatusTag, breakpoints, useMediaQuery } from '@coursera/cds-core';

import type { PartnerSpecializationFragment } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerSpecializationsQuery';
import { sortAndFilterSpecializations } from 'bundles/partners-graphql/components/cds/specializationUtils';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import { getSdpUrl } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import { LEARNING_PRODUCTS } from 'bundles/search-common/constants';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  showMoreButton: css`
    width: auto !important;
    max-width: none !important;
    align-self: flex-start;
  `,
  sectionPadding: css`
    padding: var(--cds-spacing-300) 0;
  `,
};

const renderExpandButton = ({ onClick, onKeyDown, defaultLabel }: ExpandButtonProps) => (
  <Button css={styles.showMoreButton} size="small" variant="secondary" onClick={onClick} onKeyDown={onKeyDown}>
    {defaultLabel}
  </Button>
);

type Props = {
  specializations: PartnerSpecializationFragment[];
  title?: string;
};

const SpecializationCardsSection = ({ specializations, title = _t('Certificates and Specializations') }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const processedSpecializations = sortAndFilterSpecializations(specializations);

  if (processedSpecializations.length === 0) {
    return null;
  }

  const cardVariant = isMobile || specializations.length <= 2 ? 'list' : 'grid';

  const renderSpecializationCard = ({
    item: specialization,
    itemIndex,
  }: {
    item: PartnerSpecializationFragment;
    itemIndex: number;
  }) => {
    const specializationUrl = getSdpUrl(specialization.productVariant, specialization.slug);

    const isComingSoon = !specialization.launchedAt && !!specialization.plannedLaunchDate;

    return (
      <LearningProductCard
        id={specialization.id}
        slug={specialization.slug}
        name={specialization.name}
        linkProps={{ href: specializationUrl }}
        variant={cardVariant}
        productType={
          specialization.productVariant === Specialization_ProductVariant.NormalS12N
            ? LEARNING_PRODUCTS.Specialization
            : LEARNING_PRODUCTS.ProfessionalCertificate
        }
        partners={specialization.partners?.map((partner) => ({
          id: partner.id,
          name: partner.name,
          logoUrl: partner.squareLogo ?? undefined,
        }))}
        ImageComponent={
          specialization.cardImageUrl ? (
            <LearningProductCardImage imageUrl={specialization.cardImageUrl} variant={cardVariant} />
          ) : undefined
        }
        avgProductRating={specialization.rating?.averageFiveStarRating}
        numProductRatings={specialization.rating?.ratingCount}
        statusTags={
          isComingSoon
            ? {
                tags: [<CardStatusTag key="coming-soon">{_t('Coming soon')}</CardStatusTag>],
                tagNames: ['Coming soon'],
              }
            : undefined
        }
        cardIndex={itemIndex}
      />
    );
  };

  return (
    <div data-e2e="partner-specializations-section">
      <ShowMoreGridSection
        css={styles.sectionPadding}
        title={title}
        items={processedSpecializations}
        renderItem={renderSpecializationCard}
        renderExpandButton={renderExpandButton}
        itemsGridConfig={cardVariant === 'list' ? { xs: 12, sm: 6, md: 6, lg: 6 } : { xs: 12, sm: 6, md: 4, lg: 3 }}
        defaultRows={isMobile ? 2 : 1}
        showMoreAmount={8}
        spacing={24}
      />
    </div>
  );
};

export default SpecializationCardsSection;
